/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useState } from 'react';

import { IconButton } from '@atlaskit/button/new';
import { cssMap, jsx } from '@atlaskit/css';
import { Label, RequiredAsterisk } from '@atlaskit/form';
import StarStarredIcon from '@atlaskit/icon/core/star-starred';
import StarUnstarredIcon from '@atlaskit/icon/core/star-unstarred';
import { Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import { Radio } from '@atlaskit/radio';
import { token } from '@atlaskit/tokens';
import VisuallyHidden from '@atlaskit/visually-hidden/visually-hidden';

const DEFAULT_RATING_OPTIONS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10'];

const getSafeId = (value: string): string => value.replace(/[^A-Za-z0-9_-]/g, '-');

const styles = cssMap({
	container: {
		overflowX: 'auto',
		backgroundImage: `linear-gradient(to right, ${token('elevation.surface')}, ${token('elevation.surface')}), linear-gradient(to left, ${token('elevation.surface')}, ${token('elevation.surface')}), linear-gradient(to right, ${token('elevation.shadow.overflow.spread')}, transparent), linear-gradient(to left, ${token('elevation.shadow.overflow.spread')}, transparent)`,
		backgroundPosition: 'left center, right center, left center, right center',
		backgroundRepeat: 'no-repeat',
		backgroundSize: `${token('space.100')} 100%, ${token('space.100')} 100%, ${token('space.100')} 100%, ${token('space.100')} 100%`,
		backgroundAttachment: 'local, local, scroll, scroll',
	},
	options: {
		paddingTop: token('space.100'),
		paddingBottom: token('space.100'),
	},
});

type Props = {
	label: string;
	name: string;
	id: string;
	isRequired: boolean;
	options?: string[];
};

export const RatingInput = ({ label, name, id, isRequired, options = [] }: Props): JSX.Element => {
	const ratingOptions = options.length > 0 ? options : DEFAULT_RATING_OPTIONS;
	const fieldId = getSafeId(id);
	const labelId = `${fieldId}-label`;

	const [selected, setSelected] = useState(0);
	const [hovered, setHovered] = useState(0);

	const activeRating = hovered || selected;

	return (
		<Stack space="space.050">
			<Label id={labelId} htmlFor={fieldId}>
				{label}
				{isRequired && <RequiredAsterisk />}
			</Label>
			<Box
				id={fieldId}
				xcss={styles.container}
				testId="survey-builder-rating-input"
				role="radiogroup"
				aria-labelledby={labelId}
				aria-required={isRequired}
				onMouseLeave={() => setHovered(0)}
			>
				<Box xcss={styles.options}>
					<Inline space="space.400">
						{ratingOptions.map((option, index) => {
							const ratingValue = index + 1;
							const position = String(ratingValue);
							const inputId = `${fieldId}-${getSafeId(position)}`;
							const isFilled = ratingValue <= activeRating;

							return (
								<Stack key={inputId} alignInline="center" space="space.100">
									<VisuallyHidden>
										<Radio
											id={inputId}
											value={position}
											label={position}
											name={name}
											isRequired={isRequired}
											isChecked={selected === ratingValue}
											onChange={() => setSelected(ratingValue)}
										/>
									</VisuallyHidden>
									<IconButton
										appearance="subtle"
										icon={(iconProps) =>
											isFilled ? (
												<StarStarredIcon {...iconProps} color={token('color.icon.selected')} />
											) : (
												<StarUnstarredIcon {...iconProps} color={token('color.icon.subtle')} />
											)
										}
										label={position}
										onClick={() => setSelected(ratingValue)}
										onMouseEnter={() => setHovered(ratingValue)}
										aria-hidden
										tabIndex={-1}
										testId={`survey-builder-rating-input-option-button-${position}`}
									/>
									<Text size="small" align="center">
										{position}
									</Text>
								</Stack>
							);
						})}
					</Inline>
				</Box>
			</Box>
		</Stack>
	);
};
